import { getExercise } from '../data/exercises';
import { MUSCLES, MUSCLE_IDS, SECONDARY_CREDIT, type MuscleId } from '../data/muscles';
import { daysBetween, todayIso } from '../lib/dates';
import type { WorkoutSession } from '../store/types';

export interface MuscleStatus {
  muscle: MuscleId;
  /** ISO datetime of the last session that gave this muscle direct work. */
  lastTrainedAt?: string;
  lastTrainedDate?: string;
  hoursSince?: number;
  daysSince?: number;
  /**
   * 0 → just trained and still beaten up, 1 → fully recovered and ready.
   * Anything above 1 is capped; use `readiness` for the "how overdue" number.
   */
  freshness: number;
  /** hoursSince / recoveryHours. 1.0 = exactly recovered, 2.0 = twice overdue. */
  readiness: number;
  /** Direct + discounted indirect sets over the trailing 7 days. */
  weeklySets: number;
  target: [number, number];
  volumeState: 'under' | 'in_range' | 'over';
}

/** Never trained yet — treat as maximally overdue so the planner prioritises it. */
const NEVER_TRAINED_READINESS = 4;

export function muscleStatuses(
  workouts: WorkoutSession[],
  now: Date = new Date(),
): Record<MuscleId, MuscleStatus> {
  const today = todayIso(now);
  const result = {} as Record<MuscleId, MuscleStatus>;

  // Only count sets you actually completed. Planned-but-skipped work should
  // not make a muscle look trained.
  const done = workouts
    .filter((w) => w.finishedAt)
    .sort((a, b) => (a.startedAt < b.startedAt ? 1 : -1));

  for (const id of MUSCLE_IDS) {
    const muscle = MUSCLES[id];
    let lastTrainedAt: string | undefined;
    let lastTrainedDate: string | undefined;
    let weeklySets = 0;

    for (const session of done) {
      const withinWeek = daysBetween(session.date, today) < 7;
      let directHere = false;

      for (const le of session.exercises) {
        const ex = getExercise(le.exerciseId);
        if (!ex) continue;
        const completed = le.sets.filter((s) => s.done).length;
        if (completed === 0) continue;

        const isPrimary = ex.primary.includes(id);
        const isSecondary = ex.secondary.includes(id);
        if (!isPrimary && !isSecondary) continue;

        if (isPrimary) directHere = true;
        if (withinWeek) weeklySets += completed * (isPrimary ? 1 : SECONDARY_CREDIT);
      }

      // `done` is newest-first, so the first session with direct work wins.
      if (directHere && !lastTrainedAt) {
        lastTrainedAt = session.finishedAt ?? session.startedAt;
        lastTrainedDate = session.date;
      }
    }

    const hoursSince = lastTrainedAt
      ? (now.getTime() - new Date(lastTrainedAt).getTime()) / 3_600_000
      : undefined;
    const readiness =
      hoursSince === undefined
        ? NEVER_TRAINED_READINESS
        : hoursSince / muscle.recoveryHours;

    weeklySets = Math.round(weeklySets * 10) / 10;
    const [lo, hi] = muscle.weeklySets;

    result[id] = {
      muscle: id,
      lastTrainedAt,
      lastTrainedDate,
      hoursSince,
      daysSince: lastTrainedDate ? daysBetween(lastTrainedDate, today) : undefined,
      freshness: Math.min(1, readiness),
      readiness,
      weeklySets,
      target: [lo, hi],
      volumeState: weeklySets < lo ? 'under' : weeklySets > hi ? 'over' : 'in_range',
    };
  }

  return result;
}

/**
 * How ready a set of muscles is as a group, 0–1. The planner uses this to
 * decide which of your sessions is the best one to do today: a Push day is a
 * bad idea if your chest and triceps are still wrecked from Monday.
 */
export function groupFreshness(
  statuses: Record<MuscleId, MuscleStatus>,
  muscles: MuscleId[],
): number {
  if (muscles.length === 0) return 1;
  // Weight the big movers more — sore quads should veto a leg day harder than
  // slightly sore calves.
  let weighted = 0;
  let totalWeight = 0;
  for (const m of muscles) {
    const w = MUSCLES[m].size === 'large' ? 3 : MUSCLES[m].size === 'medium' ? 2 : 1;
    weighted += (statuses[m]?.freshness ?? 1) * w;
    totalWeight += w;
  }
  return weighted / totalWeight;
}

/** Muscles sorted by how overdue they are, most overdue first. */
export function mostOverdue(
  statuses: Record<MuscleId, MuscleStatus>,
  limit = 5,
): MuscleStatus[] {
  return MUSCLE_IDS.map((id) => statuses[id])
    .sort((a, b) => b.readiness - a.readiness)
    .slice(0, limit);
}

/** Muscles that got less than their weekly minimum, biggest shortfall first. */
export function underworked(statuses: Record<MuscleId, MuscleStatus>): MuscleStatus[] {
  return MUSCLE_IDS.map((id) => statuses[id])
    .filter((s) => s.volumeState === 'under')
    .sort((a, b) => a.weeklySets - a.target[0] - (b.weeklySets - b.target[0]));
}
